"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ApiError,
  api,
  clearTokens,
  loadTokens,
  type AuthUser,
  type MarketListItem,
  type MeProfile,
  type PositionRecord,
  type WalletRow,
} from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { MarketCard } from "@/components/MarketCard";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { DashboardTabs } from "@/components/DashboardTabs";
import { TestFundsBanner } from "@/components/TestFundsBanner";
import { VirtualResetBanner } from "@/components/VirtualResetBanner";
import { PiApplicationBanner } from "@/components/PiApplicationBanner";
import { MarketsTickerBar } from "@/components/home/MarketsTickerBar";
import { OrderPlacedToast } from "@/components/OrderPlacedToast";
import { SkeletonHomeDashboard } from "@/components/Skeleton";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import {
  convertCents,
  formatBalanceForRegion,
  positionValueCents,
  type DualMoneyDisplay,
  type MoneyCurrency,
} from "@/lib/format";
import { useRegion } from "@/lib/regionContext";
import { pickPrimaryCashWallet } from "@/lib/walletPicker";
import { ClientLocaleDate } from "@/components/ClientLocaleDate";

interface State {
  status: "loading" | "ready" | "error";
  cached: AuthUser | null;
  fresh: MeProfile | null;
  positions: PositionRecord[];
  wallets: WalletRow[];
  error: string | null;
  trending: MarketListItem[];
  trendingError: string | null;
}

type Timeframe = "1D" | "1W" | "1M" | "1Y" | "YTD" | "ALL";

/**
 * /home redesign 2026-04-28 PM — Polymarket-portfolio shape.
 *
 * Earlier layout: "Welcome, X" hero + KYC/locale meta grid + orders +
 * trending. Founder feedback: too dense, the meta belongs in /settings.
 * New shape:
 *
 *   - Top row, 2-up on desktop (stacked on mobile):
 *       Left: profile card (avatar, handle, joined-date, 3 stat tiles —
 *             Positions Value / Biggest Win / Predictions count — and
 *             cash balance line)
 *       Right: P/L card (big realized+unrealized total + timeframe tabs
 *             1D/1W/1M/1Y/YTD/ALL — visual today; backend historical
 *             P/L wires up in a follow-up story)
 *
 *   - MyOrdersAndPositions (kept)
 *   - Trending markets (kept)
 *
 * Account meta (KYC, role, region) deliberately moved to /settings/account
 * — closer to where users go looking for it.
 */
export default function HomePage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [state, setState] = useState<State>({
    status: "loading",
    cached: null,
    fresh: null,
    positions: [],
    wallets: [],
    error: null,
    trending: [],
    trendingError: null,
  });
  const [timeframe, setTimeframe] = useState<Timeframe>("1D");

  useEffect(() => {
    const tokens = loadTokens();
    if (!tokens) {
      router.replace(loginUrl());
      return;
    }

    let cancelled = false;
    // Pre-existing pattern from the earlier /home: cached profile from
    // localStorage flashes into the UI while the fresh /me fetch races.
    // Single-shot, not a cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((s) => ({ ...s, cached: tokens.user }));

    Promise.all([
      api.me().then(
        (profile) => ({ kind: "profile" as const, profile }),
        (err) => ({ kind: "profile-error" as const, err }),
      ),
      api.listMarkets({ sort: "trending", limit: 6 }).then(
        (data) => ({ kind: "trending" as const, markets: data.markets }),
        (err) => ({ kind: "trending-error" as const, err }),
      ),
      api.listMyPositions().then(
        (positions) => ({ kind: "positions" as const, positions }),
        () => ({
          kind: "positions" as const,
          positions: [] as PositionRecord[],
        }),
      ),
      api.listMyWallets().then(
        (wallets) => ({ kind: "wallets" as const, wallets }),
        () => ({ kind: "wallets" as const, wallets: [] as WalletRow[] }),
      ),
    ]).then((results) => {
      if (cancelled) return;

      const [profileResult, trendingResult, positionsResult, walletsResult] =
        results;

      if (
        profileResult.kind === "profile-error" &&
        profileResult.err instanceof ApiError &&
        profileResult.err.status === 401
      ) {
        clearTokens();
        router.replace(loginUrl());
        return;
      }

      setState((prev) => ({
        ...prev,
        status: profileResult.kind === "profile-error" ? "error" : "ready",
        fresh: profileResult.kind === "profile" ? profileResult.profile : null,
        positions:
          positionsResult.kind === "positions" ? positionsResult.positions : [],
        wallets: walletsResult.kind === "wallets" ? walletsResult.wallets : [],
        error:
          profileResult.kind === "profile-error"
            ? profileResult.err instanceof Error
              ? profileResult.err.message
              : t("home.errLoadProfile")
            : null,
        trending:
          trendingResult.kind === "trending" ? trendingResult.markets : [],
        trendingError:
          trendingResult.kind === "trending-error"
            ? trendingResult.err instanceof Error
              ? trendingResult.err.message
              : t("home.errLoadTrending")
            : null,
      }));
    });

    // Refresh stat tiles on test-funds grant + on order place / cancel.
    // Same `granbay.balance.changed` event TradePanel + MyOrdersAndPositions
    // dispatch.
    const onChanged = () => {
      Promise.all([
        api.listMyPositions().catch(() => [] as PositionRecord[]),
        api.listMyWallets().catch(() => [] as WalletRow[]),
      ]).then(([positions, wallets]) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, positions, wallets }));
      });
    };
    window.addEventListener("granbay.balance.changed", onChanged);

    return () => {
      cancelled = true;
      window.removeEventListener("granbay.balance.changed", onChanged);
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const stats = useMemo(() => computeStats(state.positions), [state.positions]);
  const displayCurrency = useDisplayCurrency();
  const region = useRegion();
  // EPIC-029 STORY-1805 — fx-resolve positions/P&L for display.
  // Positions are denominated in the market currency (BRL today;
  // markets aren't multi-currency yet). When the user prefers USD
  // we divide the BRL cents by 1 USDC = N BRL to get USDC-equivalent
  // cents and show "$" instead of "R$". Until the rate loads, we
  // render BRL with R$ — better than flashing zero.
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  // 2026-05-12 — fix two bugs in the prior `showInDisplay` ternary:
  //   1. US viewers leaked R$ symbol on raw USDC numbers when the FX
  //      rate hadn't resolved.
  //   2. BR viewers NEVER got USDC→BRL conversion — the
  //      `displayCurrency !== "BRL"` clause in showInDisplay excluded
  //      them from `convertCents` entirely. So a BR user holding
  //      $290.01 USDC saw "R$290,01" labeled R$ when the actual BRL
  //      value is $290.01 × ~4.9 ≈ R$1,420.
  // New rule:
  //   - BR display + rate loaded → convert USDC → BRL, label R$
  //   - BR display + no rate yet → render raw USDC, label $ USDC
  //     (honest fallback while rate resolves)
  //   - US/global display → numbers already USDC, label $
  const canConvertBrl = usdcPerBrl !== null && usdcPerBrl > 0;
  const presentCurrency: MoneyCurrency =
    displayCurrency === "BRL" && canConvertBrl
      ? "BRL"
      : displayCurrency === "BRL"
        ? "USDC"
        : displayCurrency;

  // EPIC-029 v4 — locked dual-display rule: render the wallet truth
  // (USDC by default post STORY-1700 part 2) as primary, with the
  // user's display-currency projection as secondary. formatDualMoney
  // returns secondary=null on same-currency / rate-unavailable so the
  // tile gracefully falls back to a single leg without flashing the
  // wrong symbol.
  //
  // 2026-05-03 — wallet selection unified with the chrome surfaces
  // via `pickPrimaryCashWallet` (PR #1068). Pre-unification this
  // page used `cashWallets[0]` and could disagree with the header
  // pill / mobile bottom-nav on which wallet's balance to surface
  // for users with multiple cash wallets. The helper's preference
  // order (displayCurrency match → USDC → first cash → null) keeps
  // the dashboard cash row anchored to the same wallet the chrome
  // shows.
  // 2026-05-04 — routes through formatBalanceForRegion so non-BR
  // viewers never see R$ on the cash row. Founder reported the
  // leak with a screenshot of "R$399,94 (≈ $79.99)" on a 🇺🇸
  // viewer; the previous formatDualMoney call kept BRL primary
  // because source==BRL. Same helper the header pill consumes.
  const cashDual = useMemo<DualMoneyDisplay>(() => {
    const cash = pickPrimaryCashWallet(state.wallets, displayCurrency);
    if (!cash) {
      return formatBalanceForRegion(
        0,
        "USDC",
        displayCurrency,
        usdcPerBrl,
        region.region,
      );
    }
    return formatBalanceForRegion(
      cash.cents,
      cash.currency as "BRL" | "USD" | "USDC",
      displayCurrency,
      usdcPerBrl,
      region.region,
    );
  }, [state.wallets, displayCurrency, usdcPerBrl, region.region]);

  // Stats are USDC cents (canonical). Convert via the canonical
  // helper when the target is BRL; otherwise (USD/USDC) the cents
  // pass through 1:1 because closed-beta USDC ≈ USD.
  const presentStat = (usdcCents: number): number => {
    if (presentCurrency === "BRL") {
      return (
        (convertCents(usdcCents, "USDC", "BRL", usdcPerBrl) ?? usdcCents) / 100
      );
    }
    return usdcCents / 100;
  };
  const positionsValuePresented = presentStat(stats.positionsValueUsdcCents);
  const biggestWinPresented = presentStat(stats.biggestWinUsdcCents);
  const realizedPresented = presentStat(stats.totalRealizedPnlUsdcCents);
  const unrealizedPresented = presentStat(stats.unrealizedPnlUsdcCents);

  return (
    <>
      <SiteHeader />
      <OrderPlacedToast />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0">
          {state.status === "loading" && !state.cached && (
            <SkeletonHomeDashboard />
          )}

          {state.cached && (
            <>
              {/* 2026-04-29 — Stocktwits-style market overview ticker
                  (slice 4). Top-of-home strip of high-volume binary
                  markets with their YES price + outcome-tone color.
                  Auto-hides when no items load. */}
              <MarketsTickerBar />

              <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <ProfileCard
                  username={state.cached.username}
                  joinedAt={state.fresh?.created_at ?? null}
                  positionsValue={positionsValuePresented}
                  biggestWin={biggestWinPresented}
                  predictionsCount={stats.predictionsCount}
                  cashDual={cashDual}
                  presentCurrency={presentCurrency}
                  t={t}
                />
                <PnlCard
                  realizedPnl={realizedPresented}
                  unrealizedPnl={unrealizedPresented}
                  timeframe={timeframe}
                  onTimeframe={setTimeframe}
                  presentCurrency={presentCurrency}
                  t={t}
                />
              </div>

              <TestFundsBanner />
              <VirtualResetBanner />
              <PiApplicationBanner />

              <DashboardTabs />

              {/* 2026-05-24 — Trending markets section removed from
                  /home per founder direction. Markets discovery lives
                  at / (home) and /markets; the dashboard is the
                  account-flow surface, not a market browser. */}

              {state.status === "error" && state.error && (
                <p
                  className="mt-3 text-xs"
                  style={{ color: "var(--color-status-negative)" }}
                >
                  {t("home.profileRefreshFailedTemplate", {
                    error: state.error,
                  })}
                </p>
              )}

              {/* 2026-05-24 — three subpage entry-points appended to
                  the v1 dashboard per founder direction. Buttons link
                  to /dashboard/* sub-pages (transaction history,
                  creator subscriptions, copytraders). Their shells
                  carry a back-to-source affordance so users return to
                  this dashboard cleanly. */}
              <section className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Link
                  href="/dashboard/transactions"
                  className="flex flex-col gap-2 rounded-2xl border p-5 transition-colors no-underline"
                  style={{
                    background: "var(--color-surface)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-[15px] font-bold tracking-tight">
                      Transaction history
                    </h3>
                    <span
                      aria-hidden="true"
                      className="text-[16px] font-semibold"
                      style={{ color: "var(--color-accent)" }}
                    >
                      →
                    </span>
                  </div>
                  <p
                    className="text-[12px] leading-relaxed"
                    style={{ color: "var(--color-text2)" }}
                  >
                    Trades, deposits, withdrawals, bonuses, tips, and
                    creator subscriptions — every money + share movement.
                  </p>
                </Link>

                <Link
                  href="/dashboard/creators"
                  className="flex flex-col gap-2 rounded-2xl border p-5 transition-colors no-underline"
                  style={{
                    background: "var(--color-surface)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-[15px] font-bold tracking-tight">
                      Manage subscriptions
                    </h3>
                    <span
                      aria-hidden="true"
                      className="text-[16px] font-semibold"
                      style={{ color: "var(--color-accent)" }}
                    >
                      →
                    </span>
                  </div>
                  <p
                    className="text-[12px] leading-relaxed"
                    style={{ color: "var(--color-text2)" }}
                  >
                    Creators you follow + paid tiers. Change plans,
                    unsubscribe, or upgrade in one place.
                  </p>
                </Link>

                <Link
                  href="/dashboard/copytraders"
                  className="flex flex-col gap-2 rounded-2xl border p-5 transition-colors no-underline"
                  style={{
                    background: "var(--color-surface)",
                    borderColor: "var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-[15px] font-bold tracking-tight">
                      Copytraders
                    </h3>
                    <span
                      aria-hidden="true"
                      className="text-[16px] font-semibold"
                      style={{ color: "var(--color-accent)" }}
                    >
                      →
                    </span>
                  </div>
                  <p
                    className="text-[12px] leading-relaxed"
                    style={{ color: "var(--color-text2)" }}
                  >
                    Traders you mirror with cumulative P/L and a
                    separate transaction feed for copy-trade activity.
                  </p>
                </Link>
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
}

interface DerivedStats {
  /** USDC cents. All four values are in the canonical USDC cents unit
   * post migration 0145 + STORY-1700-part-3. Display layer converts to
   * the user's region currency via `convertCents` + `formatBalanceForRegion`.
   * Renamed 2026-05-11 from `*Brl` to reflect the actual unit (was BRL
   * cents pre-USDC-as-truth; the `Brl` suffix lingered as a vestige
   * even after the math switched to USDC). */
  positionsValueUsdcCents: number;
  biggestWinUsdcCents: number;
  predictionsCount: number;
  totalRealizedPnlUsdcCents: number;
  unrealizedPnlUsdcCents: number;
}

function computeStats(positions: PositionRecord[]): DerivedStats {
  // All sums in USDC cents. Per migration 0145 centi-share semantics:
  //   value_in_USDC_cents = floor(centi × price / 100)
  // The canonical helper `positionValueCents` (lib/format) is the same
  // shape the matcher uses. 2026-05-11 founder sweep — this function
  // previously inlined `shares × cur` without the /100, producing a
  // 100× overstated "Positions value" tile on the /home header
  // (R$21,211 instead of R$212 for leo2). Same bug class as
  // DashboardTabs / MyOrdersAndPositions / WinAutoTrigger / admin
  // unrealized PnL — all migration-0145 sweep misses.
  let positionsValueCents = 0;
  let biggestWinCents = 0;
  let totalRealizedCents = 0;
  let unrealizedCents = 0;
  for (const p of positions) {
    const realizedCents = Number(p.realizedPnlCents);
    if (Number(p.shares) > 0) {
      positionsValueCents += positionValueCents(p.shares, p.currentPriceCents);
      const valueCents = positionValueCents(p.shares, p.currentPriceCents);
      const costCents = positionValueCents(p.shares, p.avgCostCents);
      unrealizedCents += valueCents - costCents;
    }
    totalRealizedCents += realizedCents;
    if (realizedCents > biggestWinCents) {
      biggestWinCents = realizedCents;
    }
  }
  return {
    positionsValueUsdcCents: positionsValueCents,
    biggestWinUsdcCents: biggestWinCents,
    predictionsCount: positions.length,
    totalRealizedPnlUsdcCents: totalRealizedCents,
    unrealizedPnlUsdcCents: unrealizedCents,
  };
}

function ProfileCard({
  username,
  joinedAt,
  positionsValue,
  biggestWin,
  predictionsCount,
  cashDual,
  presentCurrency,
  t,
}: {
  username: string;
  joinedAt: string | null;
  positionsValue: number;
  biggestWin: number;
  predictionsCount: number;
  cashDual: DualMoneyDisplay;
  presentCurrency: MoneyCurrency;
  t: TFunction;
}) {
  // 2026-05-04 — defer locale-aware "Joined Apr 2026" to client to
  // avoid React #418 (server "Apr" en vs client "abr." pt mismatch).
  // The wrapper renders the ISO YYYY-MM-DD prefix on SSR + first
  // hydration, then upgrades to the user's locale post-mount.
  const joinedLabel = joinedAt ? (
    <ClientLocaleDate
      iso={joinedAt}
      options={{ month: "short", year: "numeric" }}
    />
  ) : (
    t("home.joinedUnknown")
  );
  return (
    <div
      className="card flex flex-col gap-5 p-6"
      style={{ background: "var(--color-surface)" }}
    >
      <div className="flex items-center gap-4">
        <div
          className="flex h-[64px] w-[64px] flex-shrink-0 items-center justify-center rounded-full text-2xl font-extrabold text-white"
          style={{ background: "linear-gradient(135deg, #6639ba, #ec4899)" }}
        >
          {username[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[22px] font-bold tracking-tight">
            {username}
          </h1>
          <p className="mt-0.5 text-[12px] text-[var(--color-text3)]">
            {joinedAt ? (
              <>
                {t("home.joinedTemplate", { when: "" }).replace(/\s+$/, "")}{" "}
                {joinedLabel}
              </>
            ) : (
              t("home.joinedTemplate", { when: t("home.joinedUnknown") })
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat
          value={fmtMoneyIn(positionsValue, presentCurrency)}
          label={t("home.positionsValue")}
        />
        <Stat
          value={biggestWin > 0 ? fmtMoneyIn(biggestWin, presentCurrency) : "—"}
          label={t("home.biggestWin")}
        />
        <Stat value={String(predictionsCount)} label={t("home.predictions")} />
      </div>

      <div className="flex items-center justify-between text-[13px]">
        <span className="text-[var(--color-text3)]">
          {t("home.cashBalance")}
        </span>
        <span className="font-mono font-semibold tabular-nums">
          {cashDual.primary}
          {cashDual.secondary !== null ? (
            <span className="ml-1 font-normal text-[var(--color-text3)]">
              (≈ {cashDual.secondary})
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

function PnlCard({
  realizedPnl,
  unrealizedPnl,
  timeframe,
  onTimeframe,
  presentCurrency,
  t,
}: {
  realizedPnl: number;
  unrealizedPnl: number;
  timeframe: Timeframe;
  onTimeframe: (t: Timeframe) => void;
  presentCurrency: MoneyCurrency;
  t: TFunction;
}) {
  const total = realizedPnl + unrealizedPnl;
  const positive = total >= 0;
  return (
    <div
      className="card flex min-h-[228px] flex-col p-6"
      style={{ background: "var(--color-surface)" }}
    >
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{
              background: positive
                ? "var(--color-status-positive)"
                : "var(--color-status-negative)",
            }}
          />
          <span className="text-[12px] font-semibold text-[var(--color-text2)]">
            {t("home.profitLoss")}
          </span>
        </div>
        <div className="flex gap-0.5 rounded-md bg-[var(--color-bg2)] p-0.5 text-[11px] font-semibold">
          {(["1D", "1W", "1M", "1Y", "YTD", "ALL"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTimeframe(t)}
              className="min-h-[36px] rounded px-2 py-1 transition-colors md:min-h-0"
              style={{
                background:
                  t === timeframe ? "var(--color-brand-tint)" : "transparent",
                color:
                  t === timeframe ? "var(--color-brand)" : "var(--color-text3)",
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <div
          className="font-mono text-[40px] font-bold tabular-nums"
          style={{
            color: positive
              ? "var(--color-status-positive)"
              : "var(--color-status-negative)",
          }}
        >
          {positive ? "+" : ""}
          {fmtMoneyIn(total, presentCurrency)}
        </div>
        <div className="mt-1 text-[12px] text-[var(--color-text3)]">
          {tfCaption(timeframe, t)} · {t("home.realizedPrefix")}{" "}
          {fmtMoneyIn(realizedPnl, presentCurrency)} ·{" "}
          {t("home.unrealizedPrefix")}{" "}
          {fmtMoneyIn(unrealizedPnl, presentCurrency)}
        </div>
      </div>

      <div className="mt-auto pt-4">
        <div
          className="h-12 rounded"
          style={{
            background:
              "linear-gradient(180deg, color-mix(in srgb, var(--color-brand) 15%, transparent), transparent)",
          }}
          aria-hidden
        />
        <p className="mt-2 text-center text-[10px] text-[var(--color-text3)]">
          {t("home.chartComingSoon")}
        </p>
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-mono text-[18px] font-bold tabular-nums leading-tight">
        {value}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </div>
    </div>
  );
}

function fmtMoneyIn(amount: number, currency: MoneyCurrency): string {
  // STORY-1805: the caller has already FX-converted (or chosen not
  // to) — we just format with the right symbol + thousand/decimal
  // separators for the target locale. Region-immersion (2026-05-21)
  // extends the locale table to MXN/CAD/INR.
  const LOCALE_BY_CURRENCY: Record<MoneyCurrency, string> = {
    BRL: "pt-BR",
    MXN: "es-MX",
    CAD: "en-CA",
    INR: "en-IN",
    USD: "en-US",
    USDC: "en-US",
  };
  // Intl.NumberFormat doesn't know "USDC" as a currency code; map it
  // to USD for the formatter, then format normally.
  const intlCurrency = currency === "USDC" ? "USD" : currency;
  return new Intl.NumberFormat(LOCALE_BY_CURRENCY[currency], {
    style: "currency",
    currency: intlCurrency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function tfCaption(timeframe: Timeframe, t: TFunction): string {
  switch (timeframe) {
    case "1D":
      return t("home.tfPastDay");
    case "1W":
      return t("home.tfPastWeek");
    case "1M":
      return t("home.tfPastMonth");
    case "1Y":
      return t("home.tfPastYear");
    case "YTD":
      return t("home.tfYearToDate");
    case "ALL":
      return t("home.tfAllTime");
  }
}
